// WalletContext — React context + provider for wallet connection state
// Pure reducer, immutable state transitions, no classes
// EVM adapter loaded directly; non-EVM adapters injected via MultiChainContext

import { createContext, useContext, useReducer, useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode, JSX } from 'react';
import { adapter as evmAdapter } from '../wallet/adapters/wagmi-latest/index.ts';
import type { ChainFamily } from '@shared/types/network.ts';
import type { ConnectionHookResult } from '../wallet/types.ts';
import type {
  ConnectedWallet,
  WalletEnvironment,
  WalletLookupResult,
  WalletProvider as WalletProviderType,
  AuthSessionState,
} from '@shared/types/wallet.ts';
import { connectAuth } from '../api/auth.ts';
import { useDevConnection } from '../dev/dev-adapter.ts';

// --- State ---

export type WalletState = Readonly<{
  readonly environment: WalletEnvironment | null;
  readonly connectedWallet: ConnectedWallet | null;
  readonly lookupResult: WalletLookupResult | null;
  readonly authState: AuthSessionState;
  readonly isConnecting: boolean;
  readonly connectionRejected: boolean;
  readonly connectionError: string | null;
}>;

export const INITIAL_WALLET_STATE: WalletState = Object.freeze({
  environment: null,
  connectedWallet: null,
  lookupResult: null,
  authState: 'disconnected',
  isConnecting: false,
  connectionRejected: false,
  connectionError: null,
});

// --- Actions ---

export type WalletAction =
  | Readonly<{ readonly type: 'SET_ENVIRONMENT'; readonly payload: WalletEnvironment }>
  | Readonly<{ readonly type: 'CONNECT_START' }>
  | Readonly<{ readonly type: 'CONNECT_SUCCESS'; readonly payload: ConnectedWallet }>
  | Readonly<{ readonly type: 'CONNECT_FAIL'; readonly payload?: string }>
  | Readonly<{ readonly type: 'SET_LOOKUP'; readonly payload: WalletLookupResult }>
  | Readonly<{ readonly type: 'SET_SESSION_ID'; readonly payload: string }>
  | Readonly<{ readonly type: 'DISCONNECT' }>
  | Readonly<{ readonly type: 'SESSION_EXPIRED' }>;

// --- Reducer (pure function) ---

export const walletReducer = (state: WalletState, action: WalletAction): WalletState => {
  switch (action.type) {
    case 'SET_ENVIRONMENT':
      return Object.freeze({ ...state, environment: action.payload });

    case 'CONNECT_START':
      return Object.freeze({ ...state, isConnecting: true, connectionRejected: false, connectionError: null });

    case 'CONNECT_SUCCESS':
      return Object.freeze({
        ...state,
        connectedWallet: action.payload,
        authState: 'active' as const,
        isConnecting: false,
        connectionRejected: false,
        connectionError: null,
      });

    case 'CONNECT_FAIL':
      return Object.freeze({ ...state, isConnecting: false, connectionRejected: true, connectionError: action.payload ?? null });

    case 'SET_LOOKUP':
      return Object.freeze({ ...state, lookupResult: action.payload });

    case 'SET_SESSION_ID':
      return Object.freeze({
        ...state,
        connectedWallet: state.connectedWallet
          ? Object.freeze({ ...state.connectedWallet, sessionId: action.payload })
          : null,
      });

    case 'DISCONNECT':
      return Object.freeze({
        ...state,
        connectedWallet: null,
        lookupResult: null,
        authState: 'disconnected' as const,
        isConnecting: false,
      });

    case 'SESSION_EXPIRED':
      return Object.freeze({ ...state, authState: 'expired' as const });

    default:
      return state;
  }
};

// --- Derive provider name from chain family + connector ---

const _deriveProvider = (
  chainFamily: ChainFamily,
  connectorId: string | undefined,
): WalletProviderType => {
  switch (chainFamily) {
    case 'evm':
      return connectorId === 'walletConnect' ? 'walletconnect' : 'injected_evm';
    case 'solana':
      return 'injected_solana';
    case 'utxo':
      return 'sats_connect';
    case 'ton':
      return 'ton_connect';
    case 'tron':
      return 'injected_tron';
    default:
      return 'injected_evm';
  }
};

// --- Context ---

type WalletContextValue = Readonly<{
  readonly state: WalletState;
  readonly dispatch: React.Dispatch<WalletAction>;
  readonly openModal: () => void;
  readonly disconnect: () => void;
  readonly walletReady: boolean;
  readonly activeChainFamily: ChainFamily;
  readonly setActiveChainFamily: (family: ChainFamily) => void;
  // Convenience shortcuts — avoids state.connectedWallet everywhere
  readonly connectedWallet: (ConnectedWallet & { readonly handle: string | null }) | null;
  readonly authState: AuthSessionState;
  readonly isReconnecting: boolean;
  // Adapter reports a connection the context hasn't synced yet (first render after connect/reload)
  readonly isWalletPending: boolean;
}>;

export const WalletContext = createContext<WalletContextValue | null>(null);

// --- Provider ---

type WalletProviderProps = Readonly<{
  readonly children: ReactNode;
}>;

export const WalletProvider = ({ children }: WalletProviderProps): JSX.Element => {
  const [state, dispatch] = useReducer(walletReducer, INITIAL_WALLET_STATE);
  const [activeChainFamily, setActiveChainFamily] = useState<ChainFamily>('evm');

  // EVM adapter — always available (non-EVM injected via MultiChainBridge override on Scan)
  // Local dev mode: a dev identity stands in for the EVM wallet (pass-through otherwise / in production)
  const evmConn = useDevConnection(evmAdapter.useConnection());

  // Map chain family → connection hook result (EVM-only at this level)
  const connectionFor = useCallback((family: ChainFamily): ConnectionHookResult | undefined => {
    if (family === 'evm') return evmConn;
    return undefined;
  }, [evmConn]);

  // Active adapter connection
  const activeConn = connectionFor(activeChainFamily) ?? evmConn;

  // Track whether we've seen a connected state per chain family
  const wasConnected = useRef(false);
  const prevFamily = useRef(activeChainFamily);

  // Reset tracking when chain family changes
  useEffect(() => {
    if (prevFamily.current !== activeChainFamily) {
      wasConnected.current = false;
      prevFamily.current = activeChainFamily;
      // If new adapter is already connected, don't dispatch DISCONNECT
      // The sync effect below will handle the new state
    }
  }, [activeChainFamily]);

  // Sync active adapter's account state → WalletContext
  useEffect(() => {
    if (activeConn.account.isConnected && activeConn.account.address) {
      wasConnected.current = true;
      dispatch({
        type: 'CONNECT_SUCCESS',
        payload: Object.freeze({
          provider: _deriveProvider(activeChainFamily, activeConn.account.connectorId),
          address: activeConn.account.address,
          chainFamily: activeChainFamily,
          connectedAt: Date.now(),
          sessionId: null,
        }),
      });
    } else if (!activeConn.account.isConnected && wasConnected.current) {
      wasConnected.current = false;
      dispatch({ type: 'DISCONNECT' });
    }
  }, [activeConn.account.isConnected, activeConn.account.address, activeConn.account.connectorId, activeChainFamily]);

  // Authenticate with server after wallet connects — creates auth session, fetches handle lookup
  useEffect(() => {
    const wallet = state.connectedWallet;
    if (!wallet || wallet.sessionId !== null) return;

    connectAuth(wallet.address, wallet.chainFamily).then(result => {
      if (result.ok) {
        dispatch({ type: 'SET_SESSION_ID', payload: result.value.sessionId });
        dispatch({ type: 'SET_LOOKUP', payload: result.value.lookup });
      }
    });
  }, [state.connectedWallet]);

  // Open wallet connection via active adapter
  // Adapters that show selection modals (Solana, TON) now reject on dismiss.
  // The .then path handles adapters where openModal resolves before connection
  // completes (shouldn't happen anymore, but safety net).
  const openModal = useCallback(() => {
    dispatch({ type: 'CONNECT_START' });
    activeConn.openModal()
      .catch((e: unknown) => {
        const msg = e instanceof Error ? e.message : undefined;
        // Filter out "User cancelled" — not an error, just a dismiss
        const isCancelled = msg?.includes('cancelled') || msg?.includes('dismissed');
        dispatch({ type: 'CONNECT_FAIL', payload: isCancelled ? undefined : msg });
      });
  }, [activeConn]);

  // Disconnect via active adapter
  const disconnect = useCallback(() => {
    activeConn.disconnect();
    dispatch({ type: 'DISCONNECT' });
  }, [activeConn]);

  // Convenience: enrich connectedWallet with handle from lookup
  const enrichedWallet = state.connectedWallet
    ? Object.freeze({
        ...state.connectedWallet,
        handle: state.lookupResult?.kind === 'registered' ? state.lookupResult.handle : null,
      })
    : null;

  return (
    <WalletContext.Provider value={{
      state, dispatch, openModal, disconnect, walletReady: true,
      activeChainFamily, setActiveChainFamily,
      connectedWallet: enrichedWallet,
      authState: state.authState,
      isReconnecting: activeConn.account.isReconnecting,
      isWalletPending: activeConn.account.isConnected && state.connectedWallet === null,
    }}>
      {children}
    </WalletContext.Provider>
  );
};

// --- Default context for when WalletProvider hasn't loaded yet ---

const _noop = (): void => {};

const DEFAULT_WALLET_CONTEXT: WalletContextValue = Object.freeze({
  state: INITIAL_WALLET_STATE,
  dispatch: _noop as unknown as React.Dispatch<WalletAction>,
  openModal: _noop,
  disconnect: _noop,
  walletReady: false,
  activeChainFamily: 'evm' as ChainFamily,
  setActiveChainFamily: _noop as unknown as (family: ChainFamily) => void,
  connectedWallet: null,
  authState: 'disconnected' as AuthSessionState,
  isReconnecting: false,
  isWalletPending: false,
});

// --- Hook ---

export const useWallet = (): WalletContextValue => {
  const context = useContext(WalletContext);
  // Returns default disconnected state when outside WalletProvider
  // (e.g., during deferred wallet loading — page renders before providers mount)
  return context ?? DEFAULT_WALLET_CONTEXT;
};
